/**
 * Chat / Realtime Pinia Store
 * Replaces src/store/modules/chat.js
 *
 * Dual-provider architecture supporting both Pusher (legacy) and Centrifugo (self-hosted).
 * The actual provider is selected at runtime via VUE_APP_REALTIME_PROVIDER environment variable.
 *
 * Both providers dispatch the SAME DOM CustomEvents, so all downstream consumers
 * (SingleCustomerChats.vue, LiveChatHome.vue, etc.) are unchanged.
 *
 * Provider selection:
 *   - VUE_APP_REALTIME_PROVIDER='centrifugo' (default) → self-hosted Centrifugo
 *   - VUE_APP_REALTIME_PROVIDER='pusher'     → Pusher SaaS (fallback)
 *
 * Token refresh:
 *   - Centrifugo: centrifuge-js auto-refreshes via getCentrifugoToken() callback
 *   - Pusher: Uses Pusher's built-in auth mechanism
 */
import { defineStore } from 'pinia'
import { createRealtimeProvider, type RealtimeProvider, type RealtimeInitParams } from '@/shared/realtimeProviders'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatState {
  provider: RealtimeProvider | null
}

// Re-export for API compatibility with existing consumers
export type { RealtimeInitParams } from '@/shared/realtimeProviders'

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useChatStore = defineStore('chat', {
  state: (): ChatState => ({
    provider: null,
  }),

  getters: {
    /**
     * Check if the realtime provider is connected.
     * Getter name maintained for backward compatibility.
     */
    isRealtimeConnected: (state): boolean => state.provider?.isConnected() ?? false,

    // Legacy alias for backward compatibility
    isPusherInitialized: (state): boolean => state.provider?.isConnected() ?? false,
  },

  actions: {
    /**
     * Initialize the realtime connection (Pusher or Centrifugo).
     * The actual provider is selected based on VUE_APP_REALTIME_PROVIDER.
     *
     * Maintained as 'initRealtime' (new name) for clarity.
     * Also responds to 'initPusher' (legacy name) for backward compatibility.
     */
    initRealtime(params: RealtimeInitParams): void {
      if (this.provider) {
        console.info('[ChatStore] Provider already initialized, skipping re-init')
        return
      }

      this.provider = createRealtimeProvider()
      this.provider.initRealtime(params)
    },

    /**
     * Legacy method name for backward compatibility.
     * Routes to initRealtime.
     */
    initPusher(params: RealtimeInitParams): void {
      this.initRealtime(params)
    },

    /**
     * Tear down the realtime connection and all subscriptions.
     * Works for both Pusher and Centrifugo.
     *
     * Maintained as 'disconnectRealtime' (new name).
     * Also responds to 'disconnectPusher' (legacy name) for backward compatibility.
     */
    disconnectRealtime(): void {
      if (!this.provider) return
      this.provider.disconnectRealtime()
      this.provider = null
    },

    /**
     * Legacy method name for backward compatibility.
     * Routes to disconnectRealtime.
     */
    disconnectPusher(): void {
      this.disconnectRealtime()
    },
  },
})
